import { forwardRef, type SelectHTMLAttributes } from "react";
import { cn } from "@/lib/cn.ts";
import { ChevronDown } from "lucide-react";

interface SelectOption { value: string; label: string; }
interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string; error?: string; hint?: string; options: SelectOption[]; placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, options, placeholder, className, id, ...props }, ref,
) {
  const selectId = id ?? `select-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <div className="flex flex-col gap-1">
      {label && <label htmlFor={selectId} className="text-[12px] font-medium text-surface-700">{label}</label>}
      <div className="relative">
        <select ref={ref} id={selectId}
          className={cn(
            "h-9 w-full appearance-none rounded-lg border border-surface-200 bg-white px-3 pr-9 text-[13px] text-surface-900",
            "focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors cursor-pointer",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-danger", className,
          )} {...props}>
          {placeholder && <option value="" disabled>{placeholder}</option>}
          {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-surface-400" />
      </div>
      {hint && !error && <p className="text-[11px] text-surface-400">{hint}</p>}
      {error && <p className="text-[11px] text-danger">{error}</p>}
    </div>
  );
});
